import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, DramaIcon, FolderSearchIcon, LayoutGridIcon, MegaphoneIcon, ScrollTextIcon, ShirtIcon, UsersIcon, VenetianMaskIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// TEATRO PRONTO™ (Italia). Fonte unica: teatro-pronto/PRODUCTO.md — promessa = consegna (1:1 con i 6 volumi).
// Promessa come THEATRE READY: un anno intero di lezioni di teatro pronte in 5 minuti.
export const PRODUCT_NAME = 'TEATRO PRONTO™';
export const heroGuarantees = ['Download digitale immediato', 'Pagamento unico', 'Nessun abbonamento', 'Garanzia di 7 giorni'];
export const stats = [{
  value: '240',
  label: 'Pagine nei 6 volumi principali'
}, {
  value: '12',
  label: 'Lezioni minuto per minuto (60\') nel Volume 1'
}, {
  value: '120',
  label: 'Giochi teatrali e riscaldamenti'
}, {
  value: '8',
  label: 'Copioni originali pronti'
}, {
  value: '5',
  label: 'Bonus inclusi'
}];
export const differenceStats = [{
  value: 'Un anno intero',
  label: 'Programmazione da settembre a giugno e 12 lezioni minuto per minuto: apri il volume e insegni'
}, {
  value: 'Da 12 a 28 alunni',
  label: 'Ruoli flessibili e narratori moltiplicabili: tutta la classe in scena, nessuno escluso'
}, {
  value: 'Nuove Indicazioni 2025',
  label: 'In linea con le Nuove Indicazioni 2025: oralità, lettura espressiva e poesia a memoria'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Ogni lezione parte da zero',
  text: 'Ore la sera a inventare attività, giochi ed esercizi di teatro, senza un percorso chiaro per tutto l\'anno.'
}, {
  title: 'Ore a cercare copioni online',
  text: 'Testi sparsi su blog e siti diversi: troppo lunghi, troppo corti, protetti da copyright o pensati per 8 bambini quando ne hai 24.'
}, {
  title: 'Ruoli che non bastano per tutti',
  text: 'Riscrivere le battute perché ogni alunno abbia la sua parte richiede serate intere.'
}, {
  title: 'Prove nel caos',
  text: 'Senza un calendario chiaro le prove diventano confusione, rumore e tempo perso.'
}, {
  title: 'Scenografia e costumi da inventare',
  text: 'Ogni anno si riparte da zero: cosa costruire, cosa chiedere alle famiglie, quale musica usare.'
}, {
  title: 'Il laboratorio da valutare e documentare',
  text: 'Griglie, giudizi e relazione per il PTOF diventano altro lavoro dopo la scuola.'
}];
export const afterItems = ['Un anno intero di teatro già organizzato, da settembre a giugno', '12 lezioni minuto per minuto pronte da usare', '120 giochi teatrali e riscaldamenti, divisi per età', '8 copioni originali con ruoli flessibili da 12 a 28 alunni', 'Piano prove di 6 settimane per arrivare sereni allo spettacolo', 'Versione laica e versione tradizionale per i copioni di Natale', 'Scenografia povera e costumi con pochi euro', 'Rubriche di valutazione e 100 giudizi pronti', 'In linea con le Nuove Indicazioni 2025'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Il teatro ti toglie le serate',
  text: 'Passi le sere e i fine settimana a preparare lezioni, scrivere copioni e organizzare le prove?'
}, {
  icon: FolderSearchIcon,
  title: 'Non trovi il copione giusto',
  text: 'Cerchi una recita adatta all\'età e al numero dei tuoi alunni, e non la trovi mai già pronta?'
}, {
  icon: UsersIcon,
  title: 'Vuoi tutti in scena',
  text: 'Ti preoccupa lasciare fuori qualcuno, o gestire gli alunni timidi e quelli che vogliono il ruolo principale?'
}, {
  icon: MegaphoneIcon,
  title: 'Le prove diventano caos',
  text: 'Vorresti un calendario chiaro e giochi pronti per tenere concentrata la classe durante le prove?'
}, {
  icon: ShirtIcon,
  title: 'Scenografia e costumi ti spaventano',
  text: 'Vorresti idee semplici, con materiali di riciclo e pochi euro, per uno spettacolo che faccia bella figura?'
}, {
  icon: LayoutGridIcon,
  title: 'Ti serve una struttura per tutto l\'anno',
  text: 'Vorresti un laboratorio teatrale organizzato, dalla prima lezione alla recita di fine anno?'
}];

export const volumes: Array<{
  number: string;
  level: string;
  classes: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volume 01',
  level: 'Anno',
  classes: 'Primaria 1ª–5ª e Secondaria 1ª–3ª',
  title: 'Programmazione annuale + 12 lezioni minuto per minuto',
  tagline: 'Tutto l\'anno di teatro a scuola, organizzato mese per mese.',
  intro: 'Il primo volume contiene la programmazione annuale del laboratorio teatrale e 12 lezioni da 60 minuti, spiegate minuto per minuto, con pagina docente e scheda alunno in 3 livelli.',
  items: ['Programmazione annuale: primaria 1ª–2ª, 3ª–5ª e secondaria', 'Mese per mese, da settembre a giugno', 'Le 3 «finestre spettacolo»: Natale, Carnevale, fine anno', 'Progetto PTOF in 1 pagina', 'Tabella di progressione', '12 lezioni minuto per minuto (60\')', 'Pagina docente + scheda alunno in 3 livelli', 'Domande di riflessione e griglia di osservazione'],
  outro: 'Dalla prima lezione («Il gruppo diventa compagnia») alla prova aperta davanti al pubblico.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 02',
  level: 'Giochi',
  classes: 'Età 6–8 · 9–11 · 11–14',
  title: '120 giochi teatrali e riscaldamenti',
  tagline: 'Un gioco pronto per ogni momento del laboratorio.',
  intro: 'Il secondo volume raccoglie 120 giochi teatrali con tempo, età, numero di alunni, passaggi e variante, e un indice per età.',
  items: ['Giochi dei nomi e rompighiaccio', 'Concentrazione e ascolto', 'Fiducia e collaborazione', 'Corpo e movimento', 'Voce e respirazione', 'Ritmo e mimo', 'Giochi di chiusura', 'Indice per età: 6–8 · 9–11 · 11–14'],
  outro: 'Riscaldamenti pronti anche per tenere concentrata la classe durante le prove.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 03',
  level: 'Copioni',
  classes: 'Primaria e Secondaria',
  title: 'Copioni pronti: 8 recite originali',
  tagline: 'Otto recite pronte da mettere in scena, con ruoli flessibili.',
  intro: 'Il terzo volume contiene 8 copioni originali con ruoli flessibili da 12 a 28 alunni (narratori moltiplicabili), durata, scenografia povera, costumi e musiche suggerite di pubblico dominio.',
  items: ['«La stella che non voleva brillare» – Natale, 1ª–2ª primaria', '«Il paese senza Natale» – Natale, 3ª–5ª primaria', '«Canto di Natale a scuola» – da Dickens, secondaria', '«Il Carnevale delle maschere smarrite» – primaria', '«Pinocchio in classe» – da Collodi, 3ª–5ª primaria', '«L\'ultimo giorno di scuola» – fine anno, 5ª primaria', '«Arrivederci, scuola media!» – fine anno, 3ª media', '«Le favole di Esopo in scena» – tutte le età'],
  outro: 'I copioni di Natale per la primaria hanno una versione laica e una versione tradizionale: scegli tu quella adatta alla tua scuola.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 04',
  level: 'Spettacolo',
  classes: 'Primaria e Secondaria',
  title: 'Dalla prova allo spettacolo',
  tagline: 'Dalla prima prova al giorno della recita, passo per passo.',
  intro: 'Il quarto volume ti guida nell\'organizzazione dello spettacolo, a partire da un piano prove di 6 settimane.',
  items: ['Piano prove di 6 settimane', 'Scegliere il copione e assegnare i ruoli senza drammi', 'Calendario delle prove e regia semplice', 'Scenografia povera con materiali di riciclo', 'Costumi con pochi euro', 'Musica, canzoni, luci e audio', 'Il giorno dello spettacolo minuto per minuto', 'Checklist e piano B per assenze e imprevisti'],
  outro: 'Arrivi al giorno della recita con tutto sotto controllo.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 05',
  level: 'Discipline',
  classes: 'Primaria e Secondaria',
  title: 'Teatro nelle discipline e inclusione',
  tagline: 'Il teatro dentro le materie e per tutti gli alunni.',
  intro: 'Il quinto volume porta il teatro in italiano, storia, inglese, educazione civica, geografia e scienze, con attenzione all\'inclusione.',
  items: ['Italiano: lettura espressiva e poesie a memoria', 'Riassunto drammatizzato', 'Storia: «interviste impossibili»', 'Inglese: mini-scene in inglese', 'Educazione civica: teatro forum su bullismo e rispetto', 'Geografia e scienze in scena', 'Inclusione BES/DSA, alunni timidi e alunni NAI', 'Adattamenti per PEI/PDP'],
  outro: 'Oralità, lettura espressiva e memoria: le competenze che le Nuove Indicazioni 2025 rafforzano.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 06',
  level: 'Valutazione',
  classes: 'Primaria e Secondaria',
  title: 'Valutazione e documentazione',
  tagline: 'Valutare e documentare il laboratorio senza perdere le serate.',
  intro: 'Il sesto volume raccoglie rubriche, griglie, autovalutazione e tutto quello che serve per documentare il progetto, con 100 giudizi pronti.',
  items: ['Rubriche per la primaria con giudizi sintetici', 'Rubriche per la secondaria in decimi', 'Griglie di osservazione', 'Autovalutazione e valutazione tra pari', 'Diario di bordo del laboratorio', 'Scheda progetto PTOF e relazione finale', 'Verifiche A/B sul linguaggio teatrale con soluzioni', '100 giudizi pronti per il documento di valutazione'],
  outro: 'Un passo finale completo per valutare e documentare con serenità.',
  note: 'Materiale didattico indipendente: non è una pubblicazione ufficiale del Ministero dell\'Istruzione e del Merito.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 volumi digitali per Primaria e Secondaria',
  text: 'Un sistema organizzato con programmazione, lezioni pronte, giochi teatrali, copioni originali, piano prove e valutazione. Apri il volume, scegli la lezione o la recita e stampa. Meno preparazione. Più tempo per i tuoi alunni.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Lezioni pronte',
  text: '12 lezioni minuto per minuto nel Volume 1, con pagina docente e scheda alunno in 3 livelli.'
}, {
  icon: ScrollTextIcon,
  title: 'Copioni originali',
  text: '8 recite originali con ruoli flessibili da 12 a 28 alunni: Natale, Carnevale, fine anno e classici.'
}, {
  icon: VenetianMaskIcon,
  title: 'Giochi teatrali',
  text: '120 giochi e riscaldamenti per età, con tempo, numero di alunni, passaggi e variante.'
}, {
  icon: DramaIcon,
  title: 'Dalla prova allo spettacolo',
  text: 'Piano prove di 6 settimane, regia semplice, scenografia povera, costumi e checklist del giorno della recita.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Valutazione pronta',
  text: 'Rubriche con giudizi sintetici per la primaria e in decimi per la secondaria, più 100 giudizi pronti.'
}, {
  icon: CalendarCheckIcon,
  title: 'Programmazione annuale',
  text: 'Mese per mese da settembre a giugno, con le tre finestre spettacolo e il progetto PTOF in 1 pagina.'
}];
export const pageTypes = ['Programmazione annuale', 'Lezioni minuto per minuto', 'Schede alunno in 3 livelli', 'Griglie di osservazione'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Natale Express: la recita in 10 giorni',
  text: 'Una recita breve, pronta da preparare in 10 giorni.',
  items: ['3 mini-recite da 5–8 minuti', 'Per 1ª primaria, 3ª primaria e scuola media', '12 poesie e filastrocche originali di Natale', 'Calendario di 10 giorni'],
  benefit: 'Anche con poco tempo, la recita si fa.'
}, {
  title: 'Lezioni di teatro per il supplente',
  text: '15 lezioni che chiunque può condurre senza preparazione.',
  items: ['15 lezioni pronte', 'Istruzioni passo per passo', 'Giochi teatrali inclusi', 'Scheda di riflessione'],
  benefit: 'Il laboratorio continua anche quando non ci sei.'
}, {
  title: 'Comunicazione con le famiglie',
  text: 'Lettere pronte per tutto quello che riguarda la recita.',
  items: ['Invito alla recita', 'Costume da casa', 'Liberatoria foto e video', 'Richiesta materiali', 'Avviso prove extra', 'Ringraziamento'],
  benefit: 'Basta compilare e inviare.'
}, {
  title: 'Poster per il laboratorio teatrale',
  text: '20 poster da stampare per l\'aula o la palestra.',
  items: ['Le parti del palco', 'Regole del pubblico', '«Sì, e…»', 'Scala delle emozioni', 'Parole del teatro', 'Ruoli in compagnia e come dare feedback'],
  benefit: 'Un laboratorio ordinato e visivo in pochi minuti.'
}, {
  title: 'Banca di monologhi e scene brevi',
  text: '30 scene e monologhi originali per 1–4 alunni.',
  items: ['30 scene e monologhi originali', 'Per 1–4 alunni', 'Livello indicato', 'Consigli di regia'],
  benefit: 'Materiale pronto per le prove, le verifiche e i piccoli gruppi.'
}];
export const BONUS_VALUE = '19 €';
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Apri TEATRO PRONTO™',
  text: 'Scarica il pacchetto e apri il volume che ti serve: lezioni, giochi, copioni o spettacolo.'
}, {
  title: 'Scegli lezione o copione',
  text: 'Trova la lezione, il gioco o la recita adatta all\'età e al numero dei tuoi alunni.'
}, {
  title: 'Preparati in pochi minuti',
  text: 'Leggi la pagina docente, assegna i ruoli e stampa le schede e i copioni.'
}, {
  title: 'Prova e vai in scena',
  text: 'Segui il piano prove e porta la tua classe sul palco con tutto pronto.'
}];
export const audienceItems = ['Insegni nella scuola Primaria o nella Secondaria di primo grado', 'Conduci un laboratorio teatrale o un progetto PTOF', 'Devi preparare le recite dell\'anno: Natale, Carnevale o fine anno', 'Insegni lettere o musica e vuoi usare il teatro in classe', 'Vuoi copioni originali con un ruolo per ogni alunno', 'Vuoi giochi teatrali pronti per età', 'Vuoi rubriche e giudizi già pronti', 'Non hai esperienza di teatro ma vuoi fare bella figura'];
export const timeValueTasks = ['Preparare le lezioni di teatro', 'Cercare e adattare un copione', 'Scrivere ruoli per tutta la classe', 'Organizzare il calendario delle prove', 'Preparare griglie e giudizi'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 volumi digitali – 240 pagine',
  detail: '12 lezioni, 120 giochi, 8 copioni e valutazione'
}, {
  label: '5 bonus senza costi aggiuntivi',
  detail: 'Natale Express, supplente, famiglie, poster e monologhi'
}, {
  label: 'Primaria 1ª–5ª e Secondaria 1ª–3ª',
  detail: 'Da 12 a 28 alunni, prove in 6 settimane'
}];
export const offerIncludes = ['Download digitale immediato', 'File PDF da salvare', 'PDF da stampare', 'Nessun prodotto fisico spedito'];
export const PRICE = '19 €';
export const PRICE_OLD = '97 €';

// Solo recensioni autentiche di TEATRO PRONTO. La sezione resta nascosta finché è vuota.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Come ricevo il materiale?',
  a: 'TEATRO PRONTO™ è un prodotto digitale. Dopo l\'acquisto ricevi l\'accesso ai file PDF da scaricare e salvare. Puoi usarli su computer, tablet o smartphone e stampare le pagine che ti servono.'
}, {
  q: 'Per quali classi è?',
  a: 'Per la Primaria (1ª–5ª) e la Secondaria di primo grado (1ª–3ª). Ogni copione indica la classe consigliata e i giochi teatrali sono divisi per età: 6–8, 9–11 e 11–14 anni.'
}, {
  q: 'Non ho mai fatto teatro. Posso usarlo?',
  a: 'Sì. Ogni lezione è spiegata minuto per minuto, con istruzioni per l\'insegnante, materiali e tempi. Non serve esperienza teatrale.'
}, {
  q: 'Quanti alunni possono recitare?',
  a: 'I copioni hanno ruoli flessibili da 12 a 28 alunni, con narratori moltiplicabili: puoi aggiungere o togliere parti in modo che tutta la classe vada in scena.'
}, {
  q: 'I copioni sono originali?',
  a: 'Sì. I copioni sono originali oppure adattamenti di classici di pubblico dominio (Collodi, Dickens, Esopo). Le musiche suggerite sono tradizionali di pubblico dominio.'
}, {
  q: 'Le recite di Natale vanno bene per tutte le scuole?',
  a: 'I copioni di Natale per la primaria hanno due versioni: una laica (il Natale come festa di pace, luci e solidarietà) e una tradizionale con il presepe vivente. Scegli quella adatta alla tua scuola.'
}, {
  q: 'È in linea con le Nuove Indicazioni?',
  a: 'Il materiale è pensato in linea con le Nuove Indicazioni 2025, che rafforzano l\'oralità, la lettura espressiva e la poesia a memoria. È un materiale didattico indipendente, non una pubblicazione ufficiale del Ministero.'
}, {
  q: 'Posso usarlo per il progetto PTOF?',
  a: 'Sì. Il Volume 1 contiene il progetto PTOF in 1 pagina e il Volume 6 la scheda progetto, il diario di bordo e la relazione finale.'
}, {
  q: 'Ci sono rubriche e giudizi per la valutazione?',
  a: 'Sì. Trovi rubriche con giudizi sintetici per la primaria e in decimi per la secondaria, griglie di osservazione, autovalutazione e 100 giudizi pronti per il documento di valutazione.'
}, {
  q: 'Devo stampare tutto?',
  a: 'No. Scegli tu quali pagine stampare e puoi usare parte del materiale in digitale.'
}, {
  q: 'I 5 bonus sono inclusi?',
  a: `Sì. Tutti e cinque i bonus sono inclusi senza costi aggiuntivi – tutto a ${PRICE}.`
}, {
  q: 'È un abbonamento?',
  a: 'No. Paghi una sola volta e ricevi l\'accesso al materiale digitale. Nessun costo ricorrente.'
}, {
  q: 'C\'è una garanzia?',
  a: 'Sì. TEATRO PRONTO™ ha una garanzia di soddisfazione di 7 giorni. Se il materiale non soddisfa le tue aspettative, puoi contattarci e chiedere il rimborso entro il periodo di garanzia, secondo le condizioni.'
}];
